import { useState, useEffect, useCallback } from "react";
import {
  isPushSupported,
  getNotificationPermission,
  requestNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush,
  sendSubscriptionToBackend,
  removeSubscriptionFromBackend,
  showTestNotification,
} from "@/services/push/pushService";

export interface UsePushNotificationReturn {
  isSupported: boolean;
  permission: NotificationPermission;
  isSubscribed: boolean;
  isLoading: boolean;
  error: string | null;
  requestPermission: () => Promise<NotificationPermission>;
  subscribe: () => Promise<boolean>;
  unsubscribe: () => Promise<boolean>;
  showTestNotification: (title?: string, body?: string) => Promise<void>;
}

export function usePushNotification(): UsePushNotificationReturn {
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>("default");
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check support and permission on mount
  useEffect(() => {
    const checkSupport = async () => {
      const supported = isPushSupported();
      setIsSupported(supported);

      if (supported) {
        const currentPermission = getNotificationPermission();
        setPermission(currentPermission);

        // Check if already subscribed
        if (currentPermission === "granted") {
          try {
            const registration = await navigator.serviceWorker.ready;
            const subscription =
              await registration.pushManager.getSubscription();
            setIsSubscribed(!!subscription);
          } catch {
            // Ignore errors during initial check
          }
        }
      }
    };

    checkSupport();
  }, []);

  // Listen for service worker messages
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "PUSH_SUBSCRIPTION_CHANGED") {
        setIsSubscribed(event.data.subscribed);
      }
    };

    navigator.serviceWorker.addEventListener("message", handleMessage);

    return () => {
      navigator.serviceWorker.removeEventListener("message", handleMessage);
    };
  }, []);

  const requestPermission = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await requestNotificationPermission();
      setPermission(result);

      if (result === "granted") {
        // Automatically subscribe after granting permission
        const registration = await navigator.serviceWorker.ready;
        const subscription = await subscribeToPush(registration);

        if (subscription) {
          await sendSubscriptionToBackend(subscription);
          setIsSubscribed(true);
        }
      } else if (result === "denied") {
        setError("Notification permission denied");
      }

      return result;
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to request permission";
      setError(message);
      return "denied";
    } finally {
      setIsLoading(false);
    }
  }, []);

  const subscribe = useCallback(async () => {
    if (permission !== "granted") {
      const result = await requestPermission();
      if (result !== "granted") return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await subscribeToPush(registration);

      if (subscription) {
        const success = await sendSubscriptionToBackend(subscription);
        setIsSubscribed(success);
        return success;
      }

      return false;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to subscribe";
      setError(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [permission, requestPermission]);

  const unsubscribe = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription =
        await registration.pushManager.getSubscription();

      if (subscription) {
        await unsubscribeFromPush(subscription);
      }

      await removeSubscriptionFromBackend();

      setIsSubscribed(false);
      return true;
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to unsubscribe";
      setError(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleShowTestNotification = useCallback(
    async (title?: string, body?: string) => {
      await showTestNotification(title, body);
    },
    []
  );

  return {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    error,
    requestPermission,
    subscribe,
    unsubscribe,
    showTestNotification: handleShowTestNotification,
  };
}